import React, { useEffect, useRef, useState } from 'react';
import { navigate } from 'gatsby';
import {
  clickScrollHandler,
  cn,
  getCTAStyleClass,
  scrollToElement,
} from '../../../utils/helpers';
import ImageModal from '../../wrappers/image-modal';
import { useViewport } from '~/utils/viewport-provider';

interface CtaProps {
  data: {
    cta_text?: string;
    cta_url?: string;
    cta_type?: string;
    cta_position?: string;
    style?: string;
    style_class?: string;
    wr_class?: string;
    cta_subtext?: string;
    page_class?: string;
    second_cta?: {
      text: string;
      url: string;
      color?: string;
    };
    video_cta?: {
      text: string;
      html: string;
    };
    mobile?: {
      text: string;
    };
    override_behaviour?: string;
    dropdown_options?: {
      icon?: string;
      text?: string;
      url?: string;
    }[];
  };
}

/**
 * It's beyond me why initial developers decided to wrap the CTA component
 * in a block component. It's a simple component that should be used as is.
 * Nor do I understand what "partial" namespacing is supposed to mean.
 *
 * /partials/cta.tsx is the real CTA component
 * /blocks/cta.js is the wrapper
 *
 * Also the CTA component is a mess. It's doing too much.
 * If you get a chance, refactor it.
 * There's no reason for it to be bound to page_class.
 */
const CTA = ({ data }: CtaProps) => {
  const ctaTypeClass = data.cta_type ? `cta--${data.cta_type}` : '';
  const styleClass = getCTAStyleClass(data.style);
  const { isDesktop } = useViewport();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownOptions =
    data.dropdown_options?.filter(option => option?.text && option?.url) || [];
  const hasDropdownOptions = dropdownOptions.length > 0;
  const ctaLabel = data.cta_text || data.mobile?.text;

  useEffect(() => {
    if (!hasDropdownOptions) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [hasDropdownOptions]);

  const ctaClicked = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url?: string,
    useOverrideBehaviour = false
  ) => {
    if (useOverrideBehaviour && data.override_behaviour === 'Scroll Top') {
      clickScrollHandler(event);
    } else if (url?.startsWith('#')) {
      scrollToElement(event, url);
    }
  };

  return (
    <>
      {data.page_class === '404-page-not-found' && (
        <>
          {data.cta_url && (
            <a
              className={cn(`cta ${styleClass}`)}
              href={data.cta_url}
            >
              {data.cta_text}
            </a>
          )}
          <a
            className={`cta cta-secondary`}
            onClick={() => navigate(-2)}
          >
            Go back
          </a>
        </>
      )}
      {data.page_class === 'thank-you' && (
        <>
          <a
            className={`cta cta-primary`}
            onClick={() => navigate(-1)}
          >
            Go back
          </a>
          {data.cta_url && (
            <a
              className={`cta cta-secondary`}
              href={data.cta_url}
            >
              {data.cta_text}
            </a>
          )}
        </>
      )}

      {['thank-you', '404-page-not-found'].includes(data.page_class || '') ===
        false && (
        <>
          <div
            className={`cta-wr ${data.wr_class ? data.wr_class : ''}`}
            style={
              data.cta_position && isDesktop
                ? {
                    textAlign:
                      data.cta_position === 'Center' ? 'center' : 'left',
                  }
                : {}
            }
          >
            {hasDropdownOptions ? (
              <div
                ref={dropdownRef}
                className='cta-dropdown'
              >
                <button
                  type='button'
                  className={cn(
                    `cta no-arrow cta-dropdown__trigger ${ctaTypeClass} ${styleClass} ${
                      isDropdownOpen ? 'is-open' : ''
                    }`
                  )}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup='true'
                  onClick={() => setIsDropdownOpen(open => !open)}
                >
                  <span>{ctaLabel}</span>
                  <span
                    className='cta-dropdown__chevron'
                    aria-hidden='true'
                  />
                </button>
                {isDropdownOpen && (
                  <div className='cta-dropdown__menu'>
                    {dropdownOptions.map(option => (
                      <a
                        key={`${option.text}-${option.url}`}
                        className='cta-dropdown__option'
                        target='_blank'
                        href={option.url}
                        onClick={event => {
                          setIsDropdownOpen(false);
                          ctaClicked(event, option.url);
                        }}
                      >
                        {option.icon && (
                          <img
                            className='cta-dropdown__icon'
                            src={option.icon}
                            alt=''
                          />
                        )}
                        <span className='cta-dropdown__label'>
                          {option.text}
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : data.mobile && data.mobile.text ? (
              data.cta_url && (
                <>
                  <a
                    className={cn(
                      `cta ${ctaTypeClass} ${styleClass} is-desktop`
                    )}
                    href={data.cta_url}
                    onClick={event => ctaClicked(event, data.cta_url, true)}
                  >
                    {data.cta_text}
                  </a>
                  <a
                    className={cn(
                      `cta ${ctaTypeClass} ${styleClass} is-mobile`
                    )}
                    href={data.cta_url}
                    onClick={event => ctaClicked(event, data.cta_url, true)}
                  >
                    {data.mobile.text}
                  </a>
                </>
              )
            ) : (
              data.cta_url && (
                <a
                  className={cn(`cta ${ctaTypeClass} ${styleClass}`)}
                  href={data.cta_url}
                  onClick={event => ctaClicked(event, data.cta_url, true)}
                >
                  {data.cta_text}
                </a>
              )
            )}
            <SecondCta
              data={data.second_cta}
              ctaTypeClass={ctaTypeClass}
            />
            {data.video_cta?.html?.trim() && (
              <ImageModal html={data.video_cta.html}>
                <button
                  type='button'
                  className={`cta--text with-hover-shadow`}
                >
                  <span className='with-play-btn'>{data.video_cta.text}</span>
                </button>
              </ImageModal>
            )}
          </div>
          {data.cta_subtext && (
            <p
              className='cta-subtext'
              dangerouslySetInnerHTML={{ __html: data.cta_subtext }}
            ></p>
          )}
        </>
      )}
    </>
  );
};

const SecondCta = ({
  data,
  ctaTypeClass,
}: {
  data?: {
    text: string;
    url: string;
    color?: string;
  };
  ctaTypeClass: string;
}) => {
  if (!data) {
    return null;
  }

  const { text, url, color } = data;
  if (!text || !url) {
    return null;
  }

  const styleOverride: CustomCSSProperties = {};
  if (color) {
    styleOverride['--cta-secondary-bg'] = color;
  }

  return (
    <a
      className={`cta cta-secondary ${ctaTypeClass}`}
      style={styleOverride}
      href={data.url}
    >
      {data.text}
    </a>
  );
};

export default CTA;
